import { createFileRoute, Link } from "@tanstack/react-router";
import { Minus, Plus, X } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/format";

export const Route = createFileRoute("/cart")({
  head: () => ({ meta: [{ title: "Your Bag — Jul's Skin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: CartPage,
});

function CartPage() {
  const { items, setQty, remove, total } = useCart();

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="mx-auto max-w-5xl w-full px-6 py-16">
        <h1 className="display text-4xl md:text-5xl">Your bag</h1>

        {items.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="text-muted-foreground">Your bag is empty.</p>
            <Link to="/shop" className="mt-6 inline-block underline underline-offset-4">Continue shopping</Link>
          </div>
        ) : (
          <div className="mt-12 grid lg:grid-cols-[1fr_360px] gap-12">
            <ul className="divide-y divide-border border-y border-border">
              {items.map((i) => (
                <li key={i.productId + i.size} className="py-6 flex gap-6">
                  <img src={i.image} alt={i.name} className="w-24 h-28 object-cover bg-muted" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-4">
                      <div>
                        <p className="text-sm">{i.name}</p>
                        <p className="eyebrow text-muted-foreground mt-1">Size {i.size}</p>
                      </div>
                      <p className="text-sm tabular-nums">{formatPrice(i.price_cents * i.quantity)}</p>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="inline-flex items-center border border-border">
                        <button aria-label="Decrease quantity" className="p-2" onClick={() => setQty(i.productId, i.size, i.quantity - 1)}><Minus className="h-3 w-3" /></button>
                        <span className="px-3 text-sm tabular-nums">{i.quantity}</span>
                        <button aria-label="Increase quantity" className="p-2" onClick={() => setQty(i.productId, i.size, i.quantity + 1)}><Plus className="h-3 w-3" /></button>
                      </div>
                      <button onClick={() => remove(i.productId, i.size)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <aside className="border border-border p-6 h-fit space-y-4">
              <p className="eyebrow">Summary</p>
              <div className="flex justify-between text-sm"><span>Subtotal</span><span className="tabular-nums">{formatPrice(total)}</span></div>
              <div className="flex justify-between text-sm text-muted-foreground"><span>Shipping</span><span>Calculated at checkout</span></div>
              <div className="hairline pt-4 flex justify-between text-base"><span>Total</span><span className="tabular-nums">{formatPrice(total)}</span></div>
              <a
                href={`https://wa.me/919345883465?text=${encodeURIComponent(
                  "Hi! I want to place an order for:\n\n" +
                  items.map((i) => `- ${i.name} (Size ${i.size}) x${i.quantity} - ${formatPrice(i.price_cents * i.quantity)}\n${typeof window !== "undefined" ? window.location.origin : ""}/product/${i.slug}`).join("\n\n") +
                  "\n\nTotal: " + formatPrice(total)
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-[#25D366] text-white py-4 text-sm hover:opacity-90 mt-4"
              >
                Order on WhatsApp
              </a>
            </aside>
          </div>
        )}
      </div>
      <div className="flex-1" />
      <SiteFooter />
    </div>
  );
}
